import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Wine, ScanLine, History, MapPin, Users, LogOut, Coins, Warehouse, ArrowLeftRight, Receipt, ListOrdered, Package, Archive } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LegalLinks } from "@/components/LegalPage";
import { Reminders } from "@/components/Reminders";
import { SubCellars } from "@/components/SubCellars";

const links = [
  { to: "/", label: "Cellar", icon: Wine, id: "nav-inventory-link" },
  { to: "/scan", label: "Scan", icon: ScanLine, id: "nav-scan-link" },
  { to: "/history", label: "Moves", icon: History, id: "nav-history-link" },
  { to: "/sales", label: "Sales", icon: Receipt, id: "nav-sales-link" },
  { to: "/value", label: "Value", icon: Coins, id: "nav-value-link" },
  { to: "/reports", label: "Listings", icon: ListOrdered, id: "nav-reports-link" },
  { to: "/locations", label: "Locations", icon: MapPin, id: "nav-locations-link" },
  { to: "/boxes", label: "Boxes", icon: Package, id: "nav-boxes-link" },
  { to: "/removed", label: "Removed", icon: Archive, id: "nav-removed-link" },
  { to: "/caves", label: "Cellars", icon: Warehouse, id: "nav-caves-link", owner: true },
  { to: "/users", label: "Users", icon: Users, id: "nav-users-link", owner: true },
];

const itemCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 ${
    isActive ? "bg-[#5C1D24] text-[#F5F0EB]" : "text-[#A89F91] hover:text-[#F5F0EB] hover:bg-[#1E181B]"
  }`;

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const visible = links.filter((l) => !l.owner || user.role === "owner");
  const switchCave = async () => {
    if (user.role === "guest") {
      await logout();
      navigate("/login");
    } else {
      navigate("/choose-cave");
    }
  };
  return (
    <div className="min-h-screen grain relative">
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-60 flex-col border-r border-[#2E252A] bg-[#110D0F] px-4 py-8 z-20">
        <div className="px-3 mb-6">
          <p className="font-mono text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase">Cave</p>
          <h1 className="font-serif text-2xl text-[#F5F0EB]">The Cellar Book</h1>
        </div>
        <button onClick={switchCave} className="mx-1 mb-8 text-left rounded-md border border-[#2E252A] px-3 py-2.5 hover:border-[#D4AF37] transition-colors group" data-testid="switch-cave-button">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#6E665D]">{user.cave_parent ? `Cellar · ${user.cave_parent}` : "Cellar"}</p>
          <p className="text-sm text-[#D4AF37] flex items-center justify-between gap-2" data-testid="current-cave-name">{user.cave_name}<ArrowLeftRight className="w-3.5 h-3.5 text-[#6E665D] group-hover:text-[#D4AF37]" /></p>
        </button>
        <SubCellars />
        <nav className="flex flex-col gap-1">
          {visible.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} className={itemCls} data-testid={l.id}>
              <l.icon className="w-4 h-4" strokeWidth={1.5} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto px-3 pt-6 border-t border-[#2E252A]">
          <p className="text-sm text-[#F5F0EB]" data-testid="current-user-name">{user.name}</p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#6E665D]" data-testid="current-user-role">{user.role}</p>
          <button onClick={logout} className="mt-4 flex items-center gap-2 text-xs text-[#A89F91] hover:text-[#D4AF37] transition-colors" data-testid="logout-button">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
          <LegalLinks className="mt-5 flex-col" />
        </div>
      </aside>

      <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[#2E252A] bg-[#110D0F] sticky top-0 z-20">
        <h1 className="font-serif text-lg">The Cellar Book</h1>
        <button onClick={switchCave} className="ml-auto mr-4 text-xs text-[#D4AF37] flex items-center gap-1.5 truncate max-w-[45%]" data-testid="mobile-switch-cave-button">{user.cave_name}<ArrowLeftRight className="w-3 h-3" /></button>
        <button onClick={logout} className="text-[#A89F91]" data-testid="mobile-logout-button" aria-label="Sign out"><LogOut className="w-4 h-4" /></button>
      </header>

      <main className="relative z-10 md:pl-60 pb-24 md:pb-0">
        <div className="md:hidden"><SubCellars compact /></div>
        <Reminders key={user.cave_id} />
        <Outlet key={user.cave_id} />
        <LegalLinks className="md:hidden justify-center px-4 py-6" />
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 grid border-t border-[#2E252A] bg-[#110D0F]/95 backdrop-blur-xl" style={{ gridTemplateColumns: `repeat(${visible.length}, 1fr)` }}>
        {visible.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`mobile-${l.id}`}
            className={({ isActive }) => `flex flex-col items-center gap-1 py-3 text-[10px] ${isActive ? "text-[#D4AF37]" : "text-[#6E665D]"}`}>
            <l.icon className="w-5 h-5" strokeWidth={1.5} /> {l.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
